import { useTranslation } from 'react-i18next';
import { VariantProps } from 'tailwind-variants';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { cn } from '@Utils';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { open_icon } from '@Assets';
import { Badge, badgeVariants, Column, Table } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useMasterUpload } from '@CustomHooks';
import { ConfigurationFileStatus, ConfigurationLink } from '@Models';

interface StatusBadgeVariant extends VariantProps<typeof badgeVariants> {
  label: string;
}

const ConfigurationList = ({
  configures,
  setLoading,
  refetch,
  readOnlyMasters = false,
}: {
  configures: ConfigurationLink[];
  setLoading: (loading: boolean) => void;
  refetch: () => void;
  readOnlyMasters?: boolean;
}) => {
  const { t } = useTranslation();

  const { uploadFileToOneDrive } = useMasterUpload();

  const getBadgeVariant = (status: ConfigurationFileStatus): StatusBadgeVariant => {
    switch (status) {
      case 'COMPLETED':
        return { label: t('Configuration.Active'), variant: 'green' };
      case 'IN_PROGRESS':
        return { label: t('Configuration.InProgress'), variant: 'yellow' };
      case 'FAILED':
        return { label: t('Configuration.Failed'), variant: 'red' };
      case 'CONFIGURING':
        return { label: t('Configuration.Configuring'), variant: 'gray' };
      default:
        return { label: t('Configuration.Active'), variant: 'green' };
    }
  };

  const buildBadges = (value: string): JSX.Element => {
    const statusBadgeVariant = getBadgeVariant(value as ConfigurationFileStatus);
    return <Badge variant={statusBadgeVariant.variant} label={statusBadgeVariant.label} showDot />;
  };

  const openFileInNewWindow = ({ link, connector }: { link: string; connector?: string }) => {
    const title = link.split('/').pop();
    sendCustomEvent('CONFIGURATION_FILE_OPENED', { title, connector });
    window.open(link, '_blank');
  };

  const uploadFile = (input: any, configurationLink: ConfigurationLink): void => {
    setLoading(true);

    const uploadAsync = async () => {
      const file = input?.target?.files?.[0];

      await uploadFileToOneDrive(
        file,
        configurationLink.id.toUpperCase(),
        configurationLink.title,
        configurationLink.connector ?? '',
        'update_master',
      );
    };

    uploadAsync()
      .then(() => {
        setLoading(false);
        refetch();
        sendCustomEvent('CONFIGURATION_FILE_REPLACED', {
          type: 'UPLOAD',
          title: configurationLink.title,
          connector: configurationLink.connector,
        });
        showSuccessToast(t('Configuration.Success'));
      })
      .catch((err) => {
        setLoading(false);
        sendErrorEvent(`unable to request upload file. ${err}`);
        showErrorToast(err.message || t('Configuration.Error'));
      });
  };

  const builderCell = (
    key: string,
    type: string,
    accessor: string,
    configurationLink: ConfigurationLink,
  ): JSX.Element => {
    const value = key in configurationLink ? configurationLink[key] : '';

    if (type === 'badge') return buildBadges((value as string) ?? 'Active');

    if (type === 'icon')
      return (
        <div className="flex  items-center">
          <img src={configurationLink.icon || ''} alt="icon" className="w-[25px] mr-2" />
          <span>{value}</span>
        </div>
      );

    if (type === 'label') {
      return <span className="font-semibold">{value}</span>;
    }

    if (type === 'button') {
      const title = configurationLink.title || '';
      const isReplaceDisabled = !configurationLink.link || readOnlyMasters;
      return (
        <div className="flex gap-2">
          <label
            htmlFor={title}
            className={cn(
              'flex items-center justify-center px-3 rounded-md m-auto mr-2 text-sm font-semibold h-10 w-auto  shadow-md ',
              isReplaceDisabled
                ? 'cursor-not-allowed bg-gray-200 ring-gray-400 ring-1 text-white'
                : 'border-solid border-gray-300 text-gray-700 hover:border-bg-gray-300 hover:bg-gray-50 cursor-pointer hover:shadow-sm',
            )}>
            {t('Configuration.Replace')}
            <input
              id={title}
              type="file"
              className="hidden"
              disabled={isReplaceDisabled}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={(e) => uploadFile(e, configurationLink)}
            />
          </label>
          <TrackedButton
            buttonName="CONFIGURATION_FILE_OPENED"
            onClick={() => openFileInNewWindow(configurationLink)}
            disabled={!configurationLink.link}
            isSubmit={false}
            className="w-[90px] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:border-none disabled:ring-0">
            <div className="flex items-center justify-center">
              <img src={open_icon} alt="icon" className="w-[15px] mr-[5px]" />
              {t('Configuration.Open')}
            </div>
          </TrackedButton>
        </div>
      );
    }
    return <div key={accessor}>{configurationLink[key]}</div>;
  };

  const getColumns = (): Column[] => {
    const columns = [
      {
        label: t('Configuration.Name'),
        accessor: 'title',
        key: 'title',
        type: 'label',
        width: 'w-[30%]',
      },
      {
        label: t('Configuration.LastUpdated'),
        accessor: 'last_update',
        key: 'last_update',
        type: 'general',
        width: 'w-[15%]',
      },
      {
        label: t('Configuration.Status'),
        accessor: 'credential.status',
        key: 'status',
        type: 'badge',
        width: 'w-[15%]',
      },
      {
        label: '',
        type: 'button',
        accessor: 'action',
        key: 'status',
        width: 'w-[20%]',
      },
    ];

    const headers = columns.map((column) => {
      return {
        Header: column.label,
        accessor: column.accessor,
        Cell: ({ row }) => {
          return builderCell(column.key, column.type, column.accessor, row.original);
        },
        width: column.width,
      };
    });
    return headers;
  };

  return (
    <div className="flex mt-[10px]">
      <Table columns={getColumns()} data={configures} pageSize={9} pagination />
    </div>
  );
};

export default ConfigurationList;
